import CandidateProfilePictureComponent from '@/public/shared/components/candidate-profile-picture-component/candidate-profile-picture-component.vue';
import { ApiClient } from '@/shared/api-client';
import { IPagination } from '@/shared/api-client/models';
import { ICandidate } from '@/shared/api-client/models/candidate';
import { ArgumentValidator, ConsoleFacade } from '@/shared/helpers';
import { IComplexId } from '@/shared/Models/Index';
import _ from 'lodash';
import { Component, Prop, Vue } from 'vue-property-decorator';
import Paginate from 'vuejs-paginate';

import { CandidateProfileModalOpener } from '@/public/services/candidate-profile-modal-opener.service';
import GenderIconComponent from '@/shared/components/gender-icon-component/gender-icon-component.vue';

import ElectionCandidateFilterComponent from '../../../shared/components/election-candidate-filter-component/election-candidate-filter-component.vue';
import { ICandidateFilterResult } from '../../../shared/components/election-candidate-filter-component/election-candidate-filter-component';
import { ICandidateFilter } from '@/shared/models/candidate-filter';
import { StringComparer } from '@/shared/helpers/string-comparer';

interface IViewModel {

    allCandidates: ICandidate[];

    pagination: IPagination;

    nbrOfCandidates: number;

    isLoaded: boolean;

    filter?: ICandidateFilterResult;

}

@Component({
    components: {
        Paginate,
        GenderIconComponent,
        CandidateProfilePictureComponent,
        ElectionCandidateFilterComponent
    }
})
export default class CandidateListComponent extends Vue {

    @Prop() public electionId!: IComplexId;
    @Prop() public showGender!: boolean;
    @Prop() public enableCandidateComparison!: boolean;
    @Prop() public favorCandidature!: boolean;
    @Prop() public linkToProfile!: boolean;
    @Prop({default: 250}) public candidateListPageSize!: number;
    @Prop({default: true}) public showProfilePictures!: boolean;
    @Prop() public candidateFilter!: ICandidateFilter;
    @Prop() public translations!: { [key: string]: string; };

    public viewModel: IViewModel = {
        allCandidates: [],
        pagination: {
            page: 1,
            pageSize: this.candidateListPageSize
        },
        nbrOfCandidates: 0,
        isLoaded: false,
        filter: undefined
    };

    public get candidates(): ICandidate[] {

        return this.applyCandidateFilter(this.viewModel.allCandidates);
    }

    public get candidateFilterEnabled(): boolean {
        return this.candidateFilter && (this.candidateFilter.enableFullTextSearch || (this.candidateFilter.filters && this.candidateFilter.filters.length > 0 ));
    }

    private apiClient: ApiClient = new ApiClient();

    public pageChanged(page: number): void {

        ConsoleFacade.log(page);

        this.viewModel.pagination.page = page;

        this.loadCandidates();
    }

    /**
     * Life cycle hook for when component is created
     */
    public async created() {

        await this.loadCandidates();

        this.viewModel.isLoaded = true;
    }

    public async openCandidateProfileModal(candidate: ICandidate, $event: MouseEvent): Promise<void> {

        if ($event) {

            $event.preventDefault();

            $event.stopImmediatePropagation();
        }

        const containerElement = this.$refs.container as Element;

        await CandidateProfileModalOpener.openAsync(containerElement, this.electionId, this.showGender, candidate, this.translations);
    }

    private async loadCandidates() {

        const pagedCandidatures = await this.apiClient.vote.listCandidatures(
            this.electionId.id,
            this.viewModel.pagination
        );

        this.viewModel.nbrOfCandidates = pagedCandidatures.totalNbrOfItems;

        ConsoleFacade.log(`Got ${pagedCandidatures.items.length} (of ${pagedCandidatures.totalNbrOfItems}) candidates, setup...`);

        // Adding candidates nominated by committee first
        this.viewModel.allCandidates = _.filter(pagedCandidatures.items, (c) => c.nominatedByCommittee);

        this.viewModel.allCandidates = this.viewModel.allCandidates.concat(
            _.filter(pagedCandidatures.items, (c) => !c.nominatedByCommittee)
        );
    }

    public async filterChanged(filter: ICandidateFilterResult): Promise<void> {

        ArgumentValidator.notNullOrEmpty(filter, `filter`);

        await this.$nextTick();

        this.viewModel.filter = filter;
    }

    public get pageCount(): number {

        if (!this.viewModel.pagination) {

            return 0;
        }

        return Math.ceil(this.viewModel.nbrOfCandidates / this.viewModel.pagination.pageSize);
    }

    public get candidateComparisonUrl(): string {

        return `/compareCandidatureAnswers/list?electionId=${this.electionId.id}&fromSearchPage=false`;
    }

    public get filterResultedInNoHits(): boolean {

        const isFiltered = this.viewModel.filter !== undefined &&
            ((this.viewModel.filter.query !== '' || this.viewModel.filter.aggregations.length > 0));

        return isFiltered && this.candidates.length <= 0;
    }

    private applyCandidateFilter(candidates: ICandidate[]): ICandidate[] {

        if (!this.viewModel.filter) {
            return candidates;
        }

        let filteredCandidates = _.clone(candidates);

        if (this.viewModel.filter.query && this.viewModel.filter.query.length > 0) {

            const query = this.viewModel.filter.query ?? '';

            filteredCandidates = filteredCandidates.filter(x => {

                // Search display name
                if (x.displayName.search(new RegExp(query, 'gi')) !== -1) {
                    return true;
                }

                // Search all filterable values
                for(const key in x.filterableProperties) {
                    if (x.filterableProperties[key] && x.filterableProperties[key].search(new RegExp(query, 'gi')) !== -1) {
                        return true;
                    }
                }

                return false;
            });
        }

        if (this.viewModel.filter.aggregations) {

            this.viewModel.filter.aggregations.forEach(f => {

                const propertyName = f.propertyName.toLowerCase();

                filteredCandidates = filteredCandidates.filter(x =>
                    x.filterableProperties &&
                    x.filterableProperties[propertyName] &&
                    f.values.findIndex(y => StringComparer.compare(y, x.filterableProperties ? x.filterableProperties[propertyName] : '')) !== -1
                );
            });
        }

        return filteredCandidates;
    }
}
